import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {getUserCurrencyWithFallback} from 'modules/meta/selectors'
import getNewLocationUrl from 'utils/getNewLocationUrl'

import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'
import {PreferencesWidget} from '@vio/shared/src/user/preferences/PreferencesWidget'

interface Props {
  hasCurrency?: boolean
  hasLanguage?: boolean
  onCurrencyChange?: (currency: string) => void
}

export const PreferencesWidgetWithStore = ({
  hasCurrency,
  hasLanguage = true,
  onCurrencyChange
}: Props) => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()

  const currency = useSelector(getUserCurrencyWithFallback)
  const {locale} = useIntl()

  const enabledCurrencies = process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
    ? getEnabledCurrencies(process.env.NEXT_PUBLIC_ENABLED_CURRENCIES)
    : []

  const changePreferencesAndRefreshPage = (params: {
    currency?: string
    language?: string
  }) => {
    const {currency, language} = params
    let searchParams: Record<string, string | undefined> = {}

    if (currency) {
      const currencySearchParams = {
        currency,
        price: undefined,
        originalCurrency: undefined
      }

      searchParams = {
        ...currencySearchParams
      }
    }

    if (language) {
      const languageSearchParams = {locale: language}
      searchParams = {
        ...languageSearchParams
      }
    }

    if (currency || language) {
      const newUrl = getNewLocationUrl({
        url: '',
        search: searchParams
      })

      window.location.href = newUrl
    }
  }

  const handlePreferencesChange = useCallback(
    async ({hasCurrencyChanged, hasLanguageChanged, currency, language}) => {
      if (hasLanguageChanged) {
        dispatchTeamEvent(
          trackEvent({
            category: Category.User,
            entity: Entity.LanguageChange,
            action: Action.Submitted
          })
        )
      }

      if (hasCurrencyChanged) {
        dispatchTeamEvent(
          trackEvent({
            category: Category.User,
            entity: Entity.CurrencyChange,
            action: Action.Submitted
          })
        )
      }
      if (onCurrencyChange) {
        if (hasLanguageChanged) {
          changePreferencesAndRefreshPage({
            language
          })
        } else if (hasCurrencyChanged) {
          onCurrencyChange?.(currency)
        }
      } else {
        setTimeout(() => {
          changePreferencesAndRefreshPage({
            currency: hasCurrencyChanged ? currency : undefined,
            language: hasLanguageChanged ? language : undefined
          })
        }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
      }
    },
    [dispatch]
  )
  const handlePreferencesLinkClick = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.LanguageCurrencyPreferencesButton,
        action: Action.Clicked
      })
    )
  }, [dispatchTeamEvent])

  return (
    <PreferencesWidget
      hasLanguage={hasLanguage}
      currentCurrency={currency}
      currentLanguage={locale}
      enabledCurrencies={enabledCurrencies}
      hasCurrency={hasCurrency}
      onPreferencesChange={handlePreferencesChange}
      onPreferencesLinkClick={handlePreferencesLinkClick}
    />
  )
}
